"use client"

import { useCallback, useEffect, useMemo, useState } from "react"

import { useMusicContext } from "@/context/musicContext"
import useViewTransition from "@/hooks/useViewTransition"

import { LISTAS } from "@/data"

import type { PlaylistCardData } from "../types"
import { sumTrackTimes } from "../utils/playlist-time"

export default function usePlaylistCard(
  data: PlaylistCardData
) {
  const navigateVT = useViewTransition()

  const {
    activeRadio,
    togglePlayPause,
    playSong,
    isPlaying: globalIsPlaying,
    currentTrackData,
  } = useMusicContext()

  const [loaded, setLoaded] = useState(false)
  const [hasError, setHasError] = useState(false)

  /* =========================
     LOAD IMAGE
  ========================= */

  useEffect(() => {
    setLoaded(false)
    setHasError(false)

    const timeout = setTimeout(() => {
      setLoaded(true)
    }, 1200)

    return () => clearTimeout(timeout)
  }, [data.imgUrl])

  /* =========================
     PLAYLIST DATA
  ========================= */

  const playlist = useMemo(() => {
    if (!data.id) return null

    return LISTAS.find(
      (p) => p.id === data.id
    ) ?? null
  }, [data.id])

  const playlistId =
    playlist?.id ?? data.id

  const playlistData =
    playlist?.playlistData ?? []

  /* =========================
     ACTIVE PLAYLIST
  ========================= */

  const isThisPlay = useMemo(() => {
    if (!playlistId) return false

    return (
      String(currentTrackData?.trackKey?.[0]) ===
      String(playlistId)
    )
  }, [
    playlistId,
    currentTrackData,
  ])

  const isPlaylistActive =
    isThisPlay &&
    activeRadio === null

  /* =========================
     PLAY STATE
  ========================= */

  const isPlaying =
    isThisPlay &&
    globalIsPlaying &&
    activeRadio === null

  /* =========================
     TOTAL TIME
  ========================= */

  const totalPlaylistTime = useMemo(() => {
    if (!playlistData.length)
      return "0m"

    return sumTrackTimes(
      playlistData
    )
  }, [playlistData])

  /* =========================
     THEME
  ========================= */

  const changeTheme = useCallback(() => {
    if (!data.hoverColor) return

    document.documentElement.style.setProperty(
      "--hover-home-bg",
      data.hoverColor
    )
  }, [data.hoverColor])

  /* =========================
     NAVIGATION
  ========================= */

  const handleNavigate = useCallback(() => {
    if (!data.link) return

    navigateVT(
      `/${data.type}/${data.link}`
    )
  }, [
    data.link,
    data.type,
    navigateVT,
  ])

  /* =========================
     PLAY / PAUSE
  ========================= */

  const handlePlay = useCallback(
    (
      e: React.MouseEvent<HTMLButtonElement>
    ) => {
      e.preventDefault()
      e.stopPropagation()

      if (!playlist) return

      // Si esta playlist ya es la activa,
      // alternar Play / Pause
      if (
        isThisPlay &&
        activeRadio === null
      ) {
        togglePlayPause()
        return
      }

      const firstSong =
        playlist.playlistData?.[0]

      if (!firstSong) return

      playSong(
        firstSong,
        0,
        playlist
      )
    },
    [
      playlist,
      isThisPlay,
      activeRadio,
      togglePlayPause,
      playSong,
    ]
  )

  return {
    loaded,
    setLoaded,

    isThisPlay,
    isPlaylistActive,
    isPlaying,

    totalPlaylistTime,

    changeTheme,
    handleNavigate,
    handlePlay,
  }
}